import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { changePassword, updateProfile } from '../../api/account.js'
import { useAuth } from '../../auth/AuthProvider.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { firstError } from '../../utils/format.js'
import MfaSettingsCard from './MfaSettingsCard.jsx'
import AdminMfaResetCard from './AdminMfaResetCard.jsx'

/**
 * Shared settings page — every authenticated role can reach this from
 * the top-bar identity link. Two cards:
 *
 *   1. Profile — edit first_name / last_name / email. Username is
 *      shown but intentionally read-only (changing it would orphan
 *      audit trails).
 *   2. Password — current + new + confirm. Backend runs the configured
 *      AUTH_PASSWORD_VALIDATORS and surfaces any failures inline.
 *
 * Role-specific settings (e.g. is_pharmacy_admin toggle, lab catalogue
 * shortcut) intentionally live elsewhere — the goal here is the
 * universal stuff every user owns.
 */
export default function SettingsPage() {
  const { user, refreshUser } = useAuth()
  const qc = useQueryClient()

  // -------- Profile form --------
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
  })
  const [profileSaved, setProfileSaved] = useState(false)

  // Active tab. Defaults to Profile for everyone (the universal surface).
  const [tab, setTab] = useState('profile')

  // -------- Doctor-only professional profile form --------
  // Clinicians own a DoctorProfile with the fields below. Patients see a
  // subset (specialty / qualifications / place_of_work / years / photo /
  // bio) on the booking page; the doctor's own letterhead and signature
  // are auto-applied to every generated PDF document.
  const isClinician = user?.role === 'doctor' || user?.role === 'specialist'
  const [clinician, setClinician] = useState({
    specialty: '',
    qualifications: '',
    place_of_work: '',
    phone_number: '',
    license_number: '',
    years_of_experience: '',
    consultation_fee: '',
    bio: '',
    available_times: '',
  })
  const [photoFile, setPhotoFile] = useState(null)
  const [signatureFile, setSignatureFile] = useState(null)
  const [clinicianSaved, setClinicianSaved] = useState(false)

  // Hydrate the form from the AuthProvider's user once it's available
  // (and re-hydrate if the upstream user changes — e.g. after a
  // refreshUser call).
  useEffect(() => {
    if (!user) return
    setProfile({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    })
    // Hydrate the clinician form from the auth /me/ payload. Coerce
    // null → "" everywhere so React's controlled inputs don't warn
    // about uncontrolled-→controlled flips.
    //
    // NOTE: the /api/auth/me/ response nests the DoctorProfile fields
    // under `profile` (see backend core/api_auth.py :: MeView.get
    // → serialize_profile). This used to read `user.doctor_profile`,
    // which is not a key the backend ever emitted — every field
    // silently rendered blank while the DB held real data (task #51,
    // 2026-07-21). Ref: feedback_silent_field_typos.
    const dp = user?.profile || {}
    setClinician({
      specialty: dp.specialty || '',
      qualifications: dp.qualifications || '',
      place_of_work: dp.place_of_work || '',
      phone_number: dp.phone_number || '',
      license_number: dp.license_number || '',
      years_of_experience:
        dp.years_of_experience != null ? String(dp.years_of_experience) : '',
      consultation_fee:
        dp.consultation_fee != null && dp.consultation_fee !== ''
          ? String(dp.consultation_fee) : '',
      bio: dp.bio || '',
      available_times: dp.available_times || '',
    })
    // Reset any in-flight file selections — they're write-only.
    setPhotoFile(null)
    setSignatureFile(null)
  }, [user?.id, user?.first_name, user?.last_name, user?.email,
      user?.profile?.specialty,
      user?.profile?.qualifications,
      user?.profile?.consultation_fee])

  const saveProfile = useMutation({
    mutationFn: () => updateProfile({
      first_name: profile.first_name,
      last_name: profile.last_name,
      email: profile.email,
    }),
    onSuccess: async () => {
      setProfileSaved(true)
      // Pull a fresh /me/ so the top-bar name + downstream queries
      // see the new values.
      await refreshUser?.()
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Auto-clear the "Saved ✓" toast after a few seconds.
      setTimeout(() => setProfileSaved(false), 2500)
    },
  })

  const saveClinician = useMutation({
    mutationFn: () => {
      const patch = {
        specialty: clinician.specialty,
        qualifications: clinician.qualifications,
        place_of_work: clinician.place_of_work,
        phone_number: clinician.phone_number,
        license_number: clinician.license_number,
        years_of_experience:
          clinician.years_of_experience === ''
            ? null : clinician.years_of_experience,
        consultation_fee:
          clinician.consultation_fee === ''
            ? null : clinician.consultation_fee,
        bio: clinician.bio,
        available_times: clinician.available_times,
      }
      // Only include file fields if the user actually picked a new one.
      if (photoFile) patch.profile_photo = photoFile
      if (signatureFile) patch.signature = signatureFile
      return updateProfile(patch)
    },
    onSuccess: async () => {
      setClinicianSaved(true)
      setPhotoFile(null)
      setSignatureFile(null)
      await refreshUser?.()
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      setTimeout(() => setClinicianSaved(false), 2500)
    },
  })

  // -------- Password form --------
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' })
  const [pwdSaved, setPwdSaved] = useState(false)
  const [pwdLocalError, setPwdLocalError] = useState('')

  const changePwd = useMutation({
    mutationFn: () => changePassword({
      currentPassword: pwd.current,
      newPassword: pwd.next,
    }),
    onSuccess: () => {
      setPwd({ current: '', next: '', confirm: '' })
      setPwdSaved(true)
      setTimeout(() => setPwdSaved(false), 3500)
    },
  })

  function onSubmitPassword(e) {
    e.preventDefault()
    setPwdLocalError('')
    if (pwd.next.length < 8) {
      setPwdLocalError('New password must be at least 8 characters.')
      return
    }
    if (pwd.next !== pwd.confirm) {
      setPwdLocalError('New passwords do not match.')
      return
    }
    if (pwd.current === pwd.next) {
      setPwdLocalError('New password must differ from the current one.')
      return
    }
    changePwd.mutate()
  }

  if (!user) {
    return <div className="p-6 text-sm text-slate-400">Loading your account…</div>
  }

  const profileDirty = (
    profile.first_name !== (user.first_name || '')
    || profile.last_name !== (user.last_name || '')
    || profile.email !== (user.email || '')
  )

  // Tabs replace the long-scroll layout. Same prominent style as the
  // org dashboard + doctor dashboard so the navigation feels consistent
  // across the app. Clinicians get an extra "Professional profile" tab;
  // everyone else sees just Profile + Password.
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)

  const TABS = [
    { id: 'profile',      label: 'Profile' },
    ...(isClinician ? [{ id: 'professional', label: 'Professional profile' }] : []),
    { id: 'password',     label: 'Password' },
    { id: 'security',     label: 'Security' },
  ]

  return (
    <div className="space-y-4 max-w-3xl">

      {/* ─── Tab strip ───────────────────────────────────────────── */}
      <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = tab === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                (active
                  ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                  : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
              }
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {/* ─── Profile ─────────────────────────────────────────────── */}
      {tab === 'profile' && (
      <Card>
        <CardHeader
          title="Profile"
          subtitle="Update the name and email shown on your account and in any documents you sign."
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput
            label="First name"
            value={profile.first_name}
            onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
            autoComplete="given-name"
          />
          <LabeledInput
            label="Last name"
            value={profile.last_name}
            onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
            autoComplete="family-name"
          />
          <LabeledInput
            label="Email"
            type="email"
            value={profile.email}
            onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
            autoComplete="email"
          />
          <LabeledInput
            label="Username (read-only)"
            value={user.username || ''}
            readOnly
            disabled
            title="Usernames can't be changed because they anchor the audit trail."
          />
        </div>

        {saveProfile.isError && (
          <div className="mt-3"><ErrorBanner error={saveProfile.error} /></div>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          {profileSaved && (
            <span className="text-emerald-700 text-xs font-medium">Saved ✓</span>
          )}
          <button
            onClick={() => saveProfile.mutate()}
            disabled={!profileDirty || saveProfile.isPending}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
          >
            {saveProfile.isPending ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </Card>
      )}

      {/* ─── Professional profile (clinicians only) ─────────────────── */}
      {tab === 'professional' && isClinician && (
        <Card>
          <CardHeader
            title="Professional profile"
            subtitle="Patients see your specialty, qualifications, place of work, years of experience, photo and bio when booking. Your signature renders on every document you generate."
          />

          {/* Public-facing fields — patients see these on the booking page */}
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mt-2 mb-2">
            Visible to patients
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <LabeledInput
              label="Specialty"
              value={clinician.specialty}
              onChange={(e) => setClinician((p) => ({ ...p, specialty: e.target.value }))}
              placeholder="e.g. General Practice, Paediatrics"
            />
            <LabeledInput
              label="Place of work"
              value={clinician.place_of_work}
              onChange={(e) => setClinician((p) => ({ ...p, place_of_work: e.target.value }))}
              placeholder="e.g. Pacific International Hospital"
            />
            <LabeledInput
              label="Qualifications"
              value={clinician.qualifications}
              onChange={(e) => setClinician((p) => ({ ...p, qualifications: e.target.value }))}
              placeholder="e.g. MBBS, MMed (Internal Medicine), FRACGP"
            />
            <LabeledInput
              label="Years of experience"
              type="number"
              min="0"
              step="1"
              value={clinician.years_of_experience}
              onChange={(e) => setClinician((p) => ({ ...p, years_of_experience: e.target.value }))}
              placeholder="e.g. 8"
            />
          </div>
          <label className="block mt-3">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Bio (a few sentences for the booking page)
            </span>
            <textarea
              rows={3}
              value={clinician.bio}
              onChange={(e) => setClinician((p) => ({ ...p, bio: e.target.value }))}
              placeholder="Brief background, areas of clinical interest, languages spoken…"
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </label>
          <label className="block mt-3">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Available times
            </span>
            <textarea
              rows={2}
              value={clinician.available_times}
              onChange={(e) => setClinician((p) => ({ ...p, available_times: e.target.value }))}
              placeholder="e.g. Mon–Fri 8:30–17:00 / Sat by appointment"
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </label>

          {/* Photo + signature — both rendered on PDFs; photo also on booking */}
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mt-5 mb-2">
            Photo & signature
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FileSlot
              label="Profile photo"
              hint="Square preferred (e.g. 400×400). Surfaces on the booking page and on document letterheads."
              currentUrl={user?.profile?.profile_photo_url}
              file={photoFile}
              onPick={setPhotoFile}
              onClear={() => setPhotoFile(null)}
            />
            <FileSlot
              label="Signature"
              hint="Transparent PNG of your wet signature, written on a tablet or scanned. Auto-applied to every generated PDF."
              currentUrl={user?.profile?.signature_url}
              file={signatureFile}
              onPick={setSignatureFile}
              onClear={() => setSignatureFile(null)}
            />
          </div>

          {/* Internal-only fields — patients DO NOT see these */}
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mt-5 mb-2">
            Practice details (not shown to patients)
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <LabeledInput
              label="Phone number"
              value={clinician.phone_number}
              onChange={(e) => setClinician((p) => ({ ...p, phone_number: e.target.value }))}
              placeholder="+675 7000 0000"
              autoComplete="tel"
            />
            <LabeledInput
              label="MSB-PNG licence number"
              value={clinician.license_number}
              onChange={(e) => setClinician((p) => ({ ...p, license_number: e.target.value }))}
              placeholder="MSB registration number"
            />
            <LabeledInput
              label="Standard consultation fee (PGK)"
              type="number"
              min="0"
              step="0.01"
              value={clinician.consultation_fee}
              onChange={(e) => setClinician((p) => ({ ...p, consultation_fee: e.target.value }))}
              placeholder="e.g. 200.00"
              autoComplete="off"
            />
          </div>

          {saveClinician.isError && (
            <div className="mt-3"><ErrorBanner error={saveClinician.error} /></div>
          )}

          <div className="mt-4 flex items-center justify-end gap-3">
            {clinicianSaved && (
              <span className="text-emerald-700 text-xs font-medium">Saved ✓</span>
            )}
            <button
              onClick={() => saveClinician.mutate()}
              disabled={saveClinician.isPending}
              className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
            >
              {saveClinician.isPending ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </Card>
      )}

      {/* ─── Password ────────────────────────────────────────────── */}
      {tab === 'password' && (
      <Card>
        <CardHeader
          title="Password"
          subtitle="Use a long, unique password. Forgot your current password? Sign out and use the “Forgot password” link on the sign-in page."
        />

        <form onSubmit={onSubmitPassword} className="space-y-3">
          <LabeledInput
            label="Current password"
            type="password"
            value={pwd.current}
            onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
            autoComplete="current-password"
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <LabeledInput
              label="New password (min 8)"
              type="password"
              value={pwd.next}
              onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <LabeledInput
              label="Confirm new password"
              type="password"
              value={pwd.confirm}
              onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          {(pwdLocalError || changePwd.isError) && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
              {pwdLocalError || firstError(changePwd.error, 'Could not change password.')}
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            {pwdSaved && (
              <span className="text-emerald-700 text-xs font-medium">Password updated ✓</span>
            )}
            <button
              type="submit"
              disabled={changePwd.isPending || !pwd.current || !pwd.next || !pwd.confirm}
              className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
            >
              {changePwd.isPending ? 'Updating…' : 'Change password'}
            </button>
          </div>
        </form>
      </Card>
      )}

      {/* ─── Security (two-factor authentication) ───────────────────── */}
      {tab === 'security' && (
        <>
          <MfaSettingsCard />
          {isAdmin && <AdminMfaResetCard />}
        </>
      )}

      {/* Footer hint — surfaces the role-specific settings entry points
          so users discover them without us bolting more cards onto this
          page. Keeps Settings tight; per-role dashboards remain the
          place where domain-specific configuration lives. */}
      <p className="text-[11px] text-slate-400 px-1">
        Looking for catalogue, queue, or organisation settings? Each
        role's dashboard owns those — try <span className="font-medium">My catalogue</span>
        on your queue page.
      </p>
    </div>
  )
}

function LabeledInput({ label, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <input
        {...rest}
        className={
          'border rounded px-2 py-1.5 text-sm '
          + (rest.disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed ' : '')
        }
      />
    </label>
  )
}

/**
 * FileSlot — file picker with preview tile for the profile photo and
 * signature uploads. Shows the currently-saved image when present, or
 * the just-picked (not-yet-uploaded) preview when the doctor selects a
 * new file.
 */
function FileSlot({ label, hint, currentUrl, file, onPick, onClear }) {
  // Build a preview URL from the picked File so the doctor sees what
  // they're about to upload. URL.createObjectURL is sync + cheap; the
  // browser auto-revokes when the tab unloads. For perfect cleanup we'd
  // useEffect(() => () => URL.revokeObjectURL(url)) — fine without it
  // for this short-lived modal-style flow.
  const previewUrl = file ? URL.createObjectURL(file) : null
  const showUrl = previewUrl || currentUrl || null
  return (
    <div className="text-xs text-slate-600 flex flex-col gap-2">
      <span>{label}</span>
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {showUrl ? (
            <img
              src={showUrl}
              alt={`${label} preview`}
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">No image</span>
          )}
        </div>
        <div className="flex flex-col gap-1 min-w-0">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
            className="text-xs"
          />
          {file && (
            <button
              type="button"
              onClick={onClear}
              className="text-[11px] text-rose-600 underline self-start"
            >
              Cancel selection
            </button>
          )}
          {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
        </div>
      </div>
    </div>
  )
}
